import { ConfigProvider } from 'shared/config/ConfigContext';
import ppData from 'shared/seo/data/pp.json';
import { ExtraLinkTree } from 'shared/seo/ExtraLinkTree';
import { LinkTree } from 'shared/seo/LinkTree';
import { PopularCities } from 'shared/seo/PopularCities';
import { PopularCountries } from 'shared/seo/PopularCountries';
import { validSeoPath } from 'shared/util/formatting';
import { DestinationPage as OldDestinationPage } from '@/components/DestinationPage';
import { cityLevel1Factory, prepackageSearchFactory } from '@/components/factory';
import { DestinationPage as NewDestinationPage } from '@/components/NewDestinationPage';
import { Offers } from '@/components/offers/Offers';
import { config, feConfig } from '@/config';
import { getCountriesForDestination, prepackageCities, prepackageCountries, prepackageMapping } from '@/data';
import { verticals } from '@/data/verticals';
import { pageContentUrl, pageFor } from '@/lib/prepackageContent';

const ppOfferUrl = config.get('prepackage.offers');
const allPagesMetadataUrl = config.get('prepackage.allCountryPlaceDestinationPagesMetadata');

const locationQueryParams = (codes: string[]): string => codes.map((code) => `to=${encodeURI(code)}`).join('&');

const fixParams = (params) => {
    const countryLevel1 = validSeoPath(params.countryLevel1);
    const cityLevel1 = validSeoPath(params.cityLevel1);

    return { countryLevel1, cityLevel1 };
};

export const getStaticPaths = async () => {
    const pageCombinations = await fetch(allPagesMetadataUrl)
        .then((res) => (res.ok ? res.json() : Promise.reject(`(${res.status}) ${res.statusText}: ${res.url}`)))
        .catch((err) => {
            console.error(err);
            return err;
        });

    const destinations = pageCombinations.map((page) => ({
        params: { cityLevel1: page.place, countryLevel1: page.country },
    }));

    return {
        paths: destinations,
        // TODO remove this when the prod database has all the entries that the dev database has.
        // I.e. the build pregenerates pages for the entries in the prod database and for entries
        // only in the dev database it will generate them on the fly with fallback set to `blocking`.
        fallback: 'blocking',
    };
};

export const getStaticProps = async (context) => {
    const { countryLevel1, cityLevel1 } = fixParams(context.params);
    const page = await pageFor(pageContentUrl.forCountryPlaceDestination(countryLevel1, cityLevel1));

    const destination = prepackageCities.find(
        (place) => place.countryPath === countryLevel1.toLowerCase() && place.cityPath === cityLevel1.toLowerCase(),
    );
    const destinationCountry = prepackageCountries.find((place) => place.countryPath === countryLevel1.toLowerCase());

    if (!page.htmlContent && (!destination || !destinationCountry)) {
        return {
            notFound: true,
        };
    }

    const popularCities = prepackageMapping[destination.id] ?? prepackageMapping[destinationCountry.id] ?? [];
    const popularCountries = getCountriesForDestination(destinationCountry, prepackageCountries);

    const offers = await fetch(`${ppOfferUrl}?${locationQueryParams(destination.locationCodes)}`)
        .then((res) => (res.ok ? res.json() : Promise.reject(`Fetching offers failed (${res.status}): ${res.url}`)))
        .catch((err) => {
            console.warn(err);
            return [];
        });

    return {
        props: {
            destination,
            content: page.htmlContent ?? null,
            popularCities,
            popularCountries,
            offers,
            config: feConfig,
        },
        revalidate: 3600,
    };
};

const CityLevel1 = ({ destination, content, popularCities, popularCountries, offers, config }) => {
    const factory = cityLevel1Factory(destination, ppData, prepackageSearchFactory());

    const DestinationPage = content ? NewDestinationPage : OldDestinationPage;

    return (
        <ConfigProvider config={config}>
            <DestinationPage factory={factory} vertical={verticals.prepackage} data={ppData}>
                {content ? <div className="mb-64" dangerouslySetInnerHTML={{ __html: content }} /> : null}
                <Offers
                    destination={destination.city}
                    offers={offers}
                    vertical={verticals.prepackage}
                    heading={ppData.lang.headingOffers}
                    showMap={false}
                />
                <PopularCities
                    destination={destination}
                    cities={popularCities}
                    cdnUrl={ppData.cdnPath}
                    verticalUrl={ppData.basePath}
                    lang={ppData.lang}
                />
                <PopularCountries
                    destination={destination}
                    countries={popularCountries}
                    cdnUrl={ppData.cdnPath}
                    verticalUrl={ppData.basePath}
                    lang={ppData.lang}
                />
                <LinkTree
                    heading={ppData.lang.linkTree.heading}
                    description={ppData.lang.linkTree.description}
                    data={ppData.linkTree}
                    url={ppData.basePath}
                />
                <ExtraLinkTree
                    heading={ppData.lang.extraLinkTree.heading}
                    linkPrefix={ppData.lang.extraLinkTree.prefix}
                    data={ppData.extraLinkTree}
                    url={ppData.basePath}
                />
            </DestinationPage>
        </ConfigProvider>
    );
};

export default CityLevel1;
